import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { authUtils } from '../utils/auth';
import { api } from '../services/api';
import { IMAGES } from '../config/images';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    // Check if token exists and validate it
    const validateExistingToken = async () => {
      const token = authUtils.getToken();

      if (!token) {
        if (mounted) setIsValidating(false);
        return;
      }

      try {
        // Try to validate token with backend
        await api.validateToken(token);
        // Token is valid, redirect to dashboard
        if (mounted) {
          navigate('/admin/dashboard', { replace: true });
        }
      } catch (error) {
        console.log('Token validation failed on login page:', error);
        // If validation fails but token exists, still redirect
        // (backend validation endpoint may have issues)
        if (token && mounted) {
          console.log('Token exists, redirecting to dashboard anyway');
          navigate('/admin/dashboard', { replace: true });
        } else {
          // No token or component unmounted
          authUtils.removeToken();
          if (mounted) setIsValidating(false);
        }
      }
    };

    validateExistingToken();
    
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setStatus({ type: '', message: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus({ type: '', message: '' });

    try {
      console.log('Attempting login...');
      const response = await api.login(formData.username, formData.password);
      console.log('Login response:', response);
      
      // Store token - check both response.token and response.data.token
      const token = response.token || response.data?.token;
      
      if (token) {
        console.log('Token received, storing...');
        authUtils.setToken(token);
        setStatus({ type: 'success', message: 'Login successful! Redirecting...' });
        
        console.log('Navigating to dashboard...');
        // Small delay to ensure token is stored
        setTimeout(() => {
          navigate('/admin/dashboard', { replace: true });
        }, 100);
      } else {
        console.error('No token in response:', response);
        throw new Error('No token received');
      }
    } catch (error) {
      console.error('Login error:', error);
      setStatus({
        type: 'error',
        message: 'Invalid username or password.'
      });
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login-page min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Admin Login | Metro Plumbing & Heating</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8 animate-fade-in-up">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <img
              src={IMAGES.logo}
              alt="Metro Plumbing & Heating"
              className="h-16 mx-auto mb-4"
            />
            <h2 className="text-3xl font-bold text-gray-900">Admin Login</h2>
            <p className="mt-2 text-gray-600">Sign in to access the dashboard</p>
          </div>

          {/* Status Message */}
          {status.message && (
            <div
              className={`mb-6 p-4 rounded-lg animate-fade-in-up ${
                status.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              <div className="flex items-start">
                <span className="text-xl mr-3">{status.type === 'success' ? '✓' : '✗'}</span>
                <p>{status.message}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-2">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter your username"
                autoComplete="username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input-field pr-10"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/admin/reset-password"
                className="text-sm font-medium text-primary hover:text-blue-700 transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Signing in...
                </>
              ) : (
                <>
                  <span className="mr-2">🔐</span>
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Security Notice */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              🔒 This is a secure admin area. All activity is logged.
            </p>
          </div>
        </div>

        {/* Back to Website Link */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-600 hover:text-primary transition-colors"
          >
            <span className="mr-2">←</span>
            Back to website
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
